/**
 * Landing records, one per runway.
 *
 * Separate from `scoring.ts`'s `RecordBook` on purpose. That one is `Record<CourseId, …>` over a
 * two-value union under `skysim.gunnery.records`, with a hard-coded `['drones','balloons']` in both
 * its constructor and its validator — a shape that says "there are exactly these courses". A
 * landing book is the opposite shape: an open set of keys, one per runway, of which there are
 * 11,776 airports' worth. Forcing one into the other would have meant widening a union that is
 * correct as it is, and migrating a stored format for no gain.
 *
 * ## Why it is capped
 *
 * The key space is unbounded, and `localStorage` is a synchronous, quota-limited store shared with
 * everything else on the origin. A pilot who tours a hundred airfields should not silently accrue
 * a hundred records and then start failing writes for the settings. {@link MAX_RUNWAYS} best
 * scores are kept and the rest are dropped, worst first — which is also the right answer for a
 * record book: nobody wants to see their 87th-best runway.
 */

import { STORAGE_KEYS, isRecord, readJson, writeJson, type StorageService } from '../core/storage.js';
import type { LandingGrade } from './modes/landing.js';

/** How many runways' records are kept. See the module note on why there is a cap at all. */
export const MAX_RUNWAYS = 40;

export interface LandingRecord {
  /** Best score on this runway, 0–100. */
  bestScore: number;
  /** The grade that came with it. */
  bestGrade: LandingGrade;
  /** How many landings have been scored here. */
  landings: number;
}

/** Keyed `ICAO/designator`, e.g. `KSFO/10L`. */
export type LandingBook = Record<string, LandingRecord>;

const GRADES: readonly LandingGrade[] = ['greaser', 'good', 'firm', 'rough', 'go around'];

/** The key a runway is filed under. One place, so the reader and the writer cannot drift. */
export function runwayKey(icao: string, designator: string): string {
  return `${icao.toUpperCase()}/${designator.toUpperCase()}`;
}

export class LandingRecordKeeper {
  private readonly storage: StorageService;
  private book: LandingBook = {};

  constructor(storage: StorageService) {
    this.storage = storage;
  }

  get records(): Readonly<LandingBook> {
    return this.book;
  }

  load(): void {
    // `readJson` narrows with a type guard; the real checking is `validate`, which rebuilds the
    // book field by field, so the guard here only has to say "this is an object at all".
    const raw = readJson(this.storage, STORAGE_KEYS.landingRecords, isRecord);
    this.book = validate(raw);
  }

  recordFor(icao: string, designator: string): Readonly<LandingRecord> | null {
    return this.book[runwayKey(icao, designator)] ?? null;
  }

  /**
   * File a landing. Returns true when it beat the previous best for that runway.
   *
   * Writes only on an improvement or a first landing, for the same reason the gunnery book does:
   * `localStorage` is synchronous, and the write that matters is the one you want to succeed.
   */
  submit(icao: string, designator: string, score: number, grade: LandingGrade): boolean {
    const key = runwayKey(icao, designator);
    const existing = this.book[key];
    const improved = existing === undefined || score > existing.bestScore;
    this.book[key] = {
      bestScore: improved ? Math.round(score) : existing.bestScore,
      bestGrade: improved ? grade : existing.bestGrade,
      landings: (existing?.landings ?? 0) + 1,
    };
    this.prune();
    writeJson(this.storage, STORAGE_KEYS.landingRecords, this.book);
    return improved;
  }

  /** Keep the best {@link MAX_RUNWAYS}, worst first out. */
  private prune(): void {
    const keys = Object.keys(this.book);
    if (keys.length <= MAX_RUNWAYS) return;
    const ranked = keys.sort((a, b) => {
      const byScore = (this.book[b]?.bestScore ?? 0) - (this.book[a]?.bestScore ?? 0);
      // Ties broken by how often it has been flown: a runway you keep coming back to is yours.
      return byScore !== 0 ? byScore : (this.book[b]?.landings ?? 0) - (this.book[a]?.landings ?? 0);
    });
    for (const key of ranked.slice(MAX_RUNWAYS)) delete this.book[key];
  }
}

/**
 * Rebuild a book from whatever came off disk, entry by entry.
 *
 * Same discipline as `scoring.ts`'s validator and for the same reason: `JSON.parse` happily yields
 * `null` for a field, `null > 0` is false while `Number(null)` is 0, and a tab closed mid-write
 * leaves a half-written record. Anything that does not survive these checks is simply not there.
 */
function validate(stored: unknown): LandingBook {
  if (typeof stored !== 'object' || stored === null || Array.isArray(stored)) return {};
  const book: LandingBook = {};
  for (const [key, raw] of Object.entries(stored as Record<string, unknown>)) {
    if (typeof raw !== 'object' || raw === null) continue;
    const entry = raw as Partial<LandingRecord>;
    const score = entry.bestScore;
    const landings = entry.landings;
    if (typeof score !== 'number' || !Number.isFinite(score) || score < 0 || score > 100) continue;
    book[key] = {
      bestScore: Math.round(score),
      bestGrade: GRADES.includes(entry.bestGrade as LandingGrade)
        ? (entry.bestGrade as LandingGrade)
        : 'firm',
      landings:
        typeof landings === 'number' && Number.isFinite(landings) && landings > 0
          ? Math.round(landings)
          : 1,
    };
  }
  return book;
}
